import { FC, useMemo } from 'react';
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  IssueTypeField,
  JiraComponent,
  JiraEpic,
  JiraPriority,
  JiraTicket,
  JiraUser,
  Nullable,
} from 'common-types';
import { MdSearch as SearchIcon } from 'react-icons/md';
import CreateJiraTicketFieldsGridWrapper from './CreateJiraTicketFieldsGridWrapper';
import CreateJiraTicketDynamicFields from './CreateJiraTicketDynamicFields';

const CreateJiraTicketAdvancedFields: FC<
  CreateJiraTicketAdvancedFieldsProps
> = ({
  jiraTicketFields,
  components,
  labels,
  onInputChange,
  loadingComponents,
  fieldsMetadata,
  dynamicTypeFieldsMetadata,
  onFieldChange,
  validationObject,
  onJiraTicketFormChange,
  prioritis,
  loadingEpics,
  onDynamicFieldChange,
  epics,
}) => {
  const { components: componentsValidation, labels: labelsValidation } =
    validationObject || {};
  const { epicLink, priority } = validationObject || {};

  const epicField = useMemo(() => {
    if (jiraTicketFields.issueType) {
      return jiraTicketFields.issueType.fields.find(field =>
        field.name.toLocaleLowerCase().includes('epic'),
      );
    }
    return null;
  }, [jiraTicketFields.issueType]);
  //console.log(epicField);
  return (
    <Stack gap={2} direction='column'>
      <CreateJiraTicketFieldsGridWrapper>
        <CreateJiraTicketDynamicFields
          dynamicTypeFieldsMetadata={dynamicTypeFieldsMetadata}
          validationObject={validationObject}
          jiraTicketFields={jiraTicketFields}
          onDynamicFieldChange={onDynamicFieldChange}
        />
        <FormControl size='small' fullWidth>
          <Autocomplete
            size='small'
            disableCloseOnSelect
            multiple
            limitTags={10}
            options={labels || []}
            getOptionLabel={option => option}
            isOptionEqualToValue={(option, value) => {
              return option === value;
            }}
            renderTags={(value: readonly string[], getTagProps) => {
              return value.map((option: string, index: number) => {
                return (
                  <Chip
                    size='small'
                    color='primary'
                    sx={{
                      height: '1.125rem',
                    }}
                    variant='outlined'
                    label={
                      <Typography variant='caption' color='text.primary'>
                        {option}
                      </Typography>
                    }
                    {...getTagProps({ index })}
                  />
                );
              });
            }}
            value={jiraTicketFields.labels || []}
            onChange={onFieldChange('labels')}
            renderInput={params => (
              <TextField
                {...params}
                error={!!labelsValidation}
                helperText={!!labelsValidation ? labelsValidation.message : ''}
                name='labels'
                hiddenLabel
                label='Labels'
                size='small'
                fullWidth
                required={
                  fieldsMetadata?.['labels']?.required &&
                  !fieldsMetadata?.['labels']?.hasDefaultValue
                }
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Typography color='text.primary' variant='body2'>
                  {option}
                </Typography>
              </li>
            )}
          />
        </FormControl>
        <FormControl size='small' fullWidth>
          <TextField
            value={jiraTicketFields.comment || ''}
            multiline
            minRows={1}
            placeholder='Add a comment... '
            size='small'
            onChange={onJiraTicketFormChange}
            name='comment'
            label='Comment'
          />
        </FormControl>
        <Tooltip
          placement='left'
          arrow
          title={
            !!!jiraTicketFields.project
              ? 'Please select project'
              : jiraTicketFields.issueType && !epicField
              ? `The 'Epic Link' field is not supported for the issue type '${jiraTicketFields.issueType.name}' in project '${jiraTicketFields.project.key}'`
              : ''
          }>
          <div>
            <FormControl size='small' fullWidth>
              <Autocomplete
                noOptionsText='Search Epic'
                popupIcon={<SearchIcon />}
                size='small'
                loading={loadingEpics}
                filterOptions={x => x}
                options={epics || []}
                disabled={!epicField || !!!jiraTicketFields.project}
                isOptionEqualToValue={(option, value) => {
                  return option.key === value.key;
                }}
                value={jiraTicketFields.epicLink}
                onChange={onFieldChange('epicLink')}
                onInputChange={onInputChange}
                getOptionLabel={option => option.fields.summary}
                renderInput={params => (
                  <TextField
                    {...params}
                    error={!!epicLink}
                    helperText={!!epicLink ? epicLink.message : ''}
                    name='epicLink'
                    color='primary'
                    label='Epic Link'
                    size='small'
                    hiddenLabel
                    fullWidth
                    required={epicField?.required && !epicField.hasDefaultValue}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Typography color='text.primary' variant='body1'>
                      {option.fields.summary}
                    </Typography>
                  </li>
                )}
              />
            </FormControl>
          </div>
        </Tooltip>
        <Tooltip
          placement='left'
          arrow
          title={
            jiraTicketFields.issueType &&
            fieldsMetadata &&
            !fieldsMetadata['priority']
              ? `The 'Priority' field is not supported for the issue type '${jiraTicketFields.issueType.name}' in project '${jiraTicketFields.project?.key}'`
              : ''
          }>
          <div>
            <FormControl size='small' fullWidth>
              <InputLabel id='priority'>Priority</InputLabel>
              <Select
                error={!!priority}
                required={
                  fieldsMetadata?.['priority']?.required &&
                  !fieldsMetadata?.['priority']?.hasDefaultValue
                }
                disabled={!!(fieldsMetadata && !fieldsMetadata['priority'])}
                value={jiraTicketFields.priority || ''}
                name='priority'
                size='small'
                labelId='priority'
                label='Priority'
                onChange={onJiraTicketFormChange}>
                {(prioritis || []).map(priority => (
                  <MenuItem key={priority.id} value={priority.id}>
                    {priority.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText error filled>
                {!!priority ? priority.message : ''}
              </FormHelperText>
            </FormControl>
          </div>
        </Tooltip>
      </CreateJiraTicketFieldsGridWrapper>
      <Tooltip
        placement='right'
        arrow
        title={
          !!!jiraTicketFields.project
            ? 'Please select project'
            : jiraTicketFields?.issueType &&
              fieldsMetadata &&
              !fieldsMetadata['components']
            ? `The 'Components' field is not supported for the issue type '${jiraTicketFields?.issueType.name}' in project '${jiraTicketFields.project.key}'`
            : ''
        }>
        <div>
          <FormControl required={true} fullWidth size='small'>
            <Autocomplete
              clearOnBlur={false}
              noOptionsText='Search Components'
              popupIcon={<SearchIcon />}
              disableCloseOnSelect
              multiple
              size='small'
              loading={loadingComponents}
              filterOptions={x => x}
              options={components || []}
              disabled={
                !!(fieldsMetadata && !fieldsMetadata['components']) ||
                !!!jiraTicketFields.project
              }
              isOptionEqualToValue={(option, value) => {
                return option.name === value.name;
              }}
              value={jiraTicketFields?.components || []}
              onChange={onFieldChange('components')}
              onInputChange={onInputChange}
              getOptionLabel={option => option.name}
              renderTags={(value: readonly JiraComponent[], getTagProps) => {
                return value.map((option: JiraComponent, index: number) => {
                  return (
                    <Chip
                      size='small'
                      color='primary'
                      sx={{
                        height: '1.125rem',
                      }}
                      variant='outlined'
                      label={
                        <Typography variant='caption' color='text.primary'>
                          {option.name}
                        </Typography>
                      }
                      {...getTagProps({ index })}
                    />
                  );
                });
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  error={!!componentsValidation}
                  helperText={
                    !!componentsValidation ? componentsValidation.message : ''
                  }
                  required={
                    fieldsMetadata?.['components']?.required &&
                    !fieldsMetadata?.['components']?.hasDefaultValue
                  }
                  name='components'
                  color='primary'
                  label='Components'
                  hiddenLabel
                  size='small'
                  fullWidth
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Typography color='text.primary' variant='body1'>
                    {option.name}
                  </Typography>
                </li>
              )}
            />
          </FormControl>
        </div>
      </Tooltip>
    </Stack>
  );
};

export interface CreateJiraTicketAdvancedFieldsProps {
  loadingEpics: boolean;
  epics: Nullable<JiraEpic[]>;
  prioritis: Nullable<JiraPriority[]>;
  jiraTicketFields: JiraTicket;
  components: Nullable<JiraComponent[]>;
  labels: Nullable<string[]>;
  loadingComponents: boolean;
  fieldsMetadata: Nullable<{
    [key: string]: IssueTypeField;
  }>;
  dynamicTypeFieldsMetadata: Nullable<IssueTypeField[]>;
  validationObject: Nullable<{
    [key: string]: {
      message: string;
    };
  }>;
  onDynamicFieldChange: (
    e: SelectChangeEvent<{
      value: string;
      id: string;
    }>,
  ) => void;
  onInputChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => void;
  onFieldChange: (
    fieldName: string,
  ) => (
    _e: React.SyntheticEvent<Element, Event>,
    value:
      | Nullable<JiraComponent[]>
      | Nullable<JiraUser>
      | Nullable<string[]>
      | Nullable<JiraEpic>,
  ) => void;
  onJiraTicketFormChange: (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>,
  ) => void;
}

export default CreateJiraTicketAdvancedFields;
